/**
 * Live Assist panel — floating bottom-left, collapsible, mounted once in AgentLayout.
 *
 * During a call: AI insights (running summary, customer intent, sentiment,
 * next-best actions, a suggested line, alerts, knowledge articles) and the
 * live transcript. After the call: the structured post-call summary, ready
 * to copy into notes / disposition.
 */
import { useCallback, useEffect, useRef, useState, type JSX, type PointerEvent as ReactPointerEvent } from 'react'
import {
  AlertTriangle, BookOpen, Bot, Check, ChevronDown, ChevronUp, ClipboardCopy, Lightbulb, Loader2, MessageSquareQuote,
  GripVertical, Mic, Pause, Play, RefreshCw, Sparkles, ThumbsDown, ThumbsUp, User, X,
} from 'lucide-react'
import type { AssistStatus, CallSummary, Insight, LiveAssistState, TranscriptSegment } from '../../modules/live-assist/useLiveAssist.js'

const STATUS: Record<AssistStatus, { label: string; dot: string }> = {
  off: { label: 'Off', dot: 'bg-gray-300' },
  idle: { label: 'Ready', dot: 'bg-gray-300' },
  connecting: { label: 'Connecting…', dot: 'bg-amber-400 animate-pulse' },
  live: { label: 'Live', dot: 'bg-emerald-500 animate-pulse' },
  reconnecting: { label: 'Reconnecting…', dot: 'bg-amber-400 animate-pulse' },
  paused: { label: 'Paused', dot: 'bg-gray-400' },
  error: { label: 'Unavailable', dot: 'bg-red-500' },
  ended: { label: 'Call ended', dot: 'bg-slate-400' },
}

const SENTIMENT: Record<string, { label: string; cls: string }> = {
  positive: { label: 'Positive', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  neutral: { label: 'Neutral', cls: 'bg-slate-50 text-slate-600 ring-slate-200' },
  negative: { label: 'Negative', cls: 'bg-orange-50 text-orange-700 ring-orange-200' },
  frustrated: { label: 'Frustrated', cls: 'bg-red-50 text-red-700 ring-red-200' },
}

const ALERT_CLS: Record<string, string> = {
  compliance: 'border-red-200 bg-red-50 text-red-800',
  escalation: 'border-orange-200 bg-orange-50 text-orange-800',
  churn_risk: 'border-amber-200 bg-amber-50 text-amber-800',
  upsell: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  info: 'border-blue-200 bg-blue-50 text-blue-800',
}

const OUTCOME: Record<string, string> = {
  resolved: 'Resolved', unresolved: 'Unresolved', follow_up: 'Follow-up needed', escalated: 'Escalated',
}

function useCopy(): [string | null, (key: string, text: string) => void] {
  const [copied, setCopied] = useState<string | null>(null)
  return [copied, (key, text) => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(key)
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500)
    })
  }]
}

function Meter({ level, Icon, label }: { level: number; Icon: typeof Mic; label: string }): JSX.Element {
  return (
    <span className="flex items-center gap-1" title={label}>
      <Icon className="h-3 w-3 text-gray-400" />
      <span className="flex h-2.5 items-end gap-[2px]">
        {[0.08, 0.2, 0.4, 0.65].map((t) => (
          <span key={t} className={`w-[3px] rounded-sm transition-all ${level > t ? 'bg-emerald-500' : 'bg-gray-200'}`} style={{ height: `${30 + t * 100}%` }} />
        ))}
      </span>
    </span>
  )
}

const POS_KEY = 'kc3.liveAssist.position'
type Pos = { x: number; y: number } | null

/**
 * Drag the panel by its header. Position is kept inside the viewport, saved
 * per browser, and re-clamped on resize; double-click the header (or null)
 * returns it to the bottom-left corner.
 */
function useDraggable(ref: React.RefObject<HTMLDivElement | null>) {
  const [pos, setPos] = useState<Pos>(() => {
    try {
      const raw = localStorage.getItem(POS_KEY)
      return raw ? (JSON.parse(raw) as Pos) : null
    } catch {
      return null
    }
  })
  const drag = useRef<{ dx: number; dy: number } | null>(null)

  const clamp = useCallback((p: { x: number; y: number }): { x: number; y: number } => {
    const el = ref.current
    const w = el?.offsetWidth ?? 380
    const h = el?.offsetHeight ?? 60
    return {
      x: Math.min(Math.max(8, p.x), Math.max(8, window.innerWidth - w - 8)),
      y: Math.min(Math.max(8, p.y), Math.max(8, window.innerHeight - Math.min(h, 120) - 8)),
    }
  }, [ref])

  useEffect(() => {
    if (pos) localStorage.setItem(POS_KEY, JSON.stringify(pos))
    else localStorage.removeItem(POS_KEY)
  }, [pos])

  useEffect(() => {
    const onResize = (): void => setPos((p) => (p ? clamp(p) : p))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [clamp])

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>): void => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return
    drag.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    e.preventDefault()
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>): void => {
    if (!drag.current) return
    setPos(clamp({ x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy }))
  }
  const onPointerUp = (e: ReactPointerEvent<HTMLElement>): void => {
    drag.current = null
    ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
  }

  return { pos, reset: () => setPos(null), handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } }
}

export default function LiveAssistPanel({ assist }: { assist: LiveAssistState }): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null)
  const { pos, reset, handlers } = useDraggable(panelRef)
  const [collapsed, setCollapsed] = useState(false)
  const [tab, setTab] = useState<'assist' | 'transcript'>('assist')
  const { config, status } = assist

  useEffect(() => { if (status === 'connecting') setCollapsed(false) }, [status])

  if (!config?.enabled || status === 'off') return null

  const st = STATUS[status]
  const finals = assist.segments.filter((s) => s.final).length

  return (
    <div
      ref={panelRef}
      className={`fixed z-[900] w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[3px] border border-gray-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.18)] ${pos ? '' : 'bottom-4 left-4'}`}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
    >
      <header
        {...handlers}
        onDoubleClick={reset}
        title="Drag to move · double-click to reset position"
        className="flex cursor-grab touch-none select-none items-center gap-2 border-b border-gray-100 bg-gradient-to-r from-[#EEF4FF] to-white px-3 py-2 active:cursor-grabbing"
      >
        <GripVertical className="-ml-1 h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />
        <span className="flex h-7 w-7 items-center justify-center rounded-[4px] bg-[#3366FF] text-white"><Sparkles className="h-3.5 w-3.5" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-semibold text-[#16273F]">Live Assist</p>
          <p className="flex items-center gap-1.5 text-[10.5px] text-gray-500">
            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
            {status === 'live' && <span className="text-gray-400">· {finals} lines</span>}
          </p>
        </div>
        {(status === 'live' || status === 'paused') && (
          <>
            <Meter level={assist.levels.agent} Icon={Mic} label="You" />
            <Meter level={assist.levels.customer} Icon={User} label="Customer" />
            <button type="button" onClick={assist.togglePause} className="rounded-[3px] p-1 text-gray-500 hover:bg-gray-100" title={assist.paused ? 'Resume transcription' : 'Pause transcription (e.g. while taking card details)'}>
              {assist.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            </button>
          </>
        )}
        <button type="button" onClick={() => setCollapsed((c) => !c)} className="rounded-[3px] p-1 text-gray-500 hover:bg-gray-100" aria-label={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {status === 'ended' && (
          <button type="button" onClick={assist.dismiss} className="rounded-[3px] p-1 text-gray-500 hover:bg-gray-100" aria-label="Close"><X className="h-4 w-4" /></button>
        )}
      </header>

      {!collapsed && (
        <div className="flex max-h-[min(70vh,620px)] flex-col" style={pos ? { maxHeight: `min(620px, calc(100vh - ${pos.y + 64}px))` } : undefined}>
          {status === 'idle' && (
            <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
              <Bot className="h-6 w-6 text-[#3366FF]" />
              <p className="text-[12.5px] text-gray-600">Get a live transcript, next-best actions and a call summary.</p>
              <button type="button" onClick={assist.start} className="rounded-[3px] bg-[#3366FF] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#2952d9]">Start live assist</button>
            </div>
          )}
          {status === 'error' && (
            <div className="m-3 rounded-[4px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{assist.error ?? 'Live assist is unavailable.'}</div>
          )}
          {status === 'ended' ? (
            <SummaryView assist={assist} />
          ) : status !== 'idle' && (
            <>
              <div className="flex border-b border-gray-100 px-2">
                {(['assist', 'transcript'] as const).map((t) => (
                  <button key={t} type="button" onClick={() => setTab(t)} className={`-mb-px border-b-2 px-3 py-1.5 text-[11.5px] font-semibold ${tab === t ? 'border-[#3366FF] text-[#3366FF]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    {t === 'assist' ? 'Assist' : `Transcript${finals ? ` (${finals})` : ''}`}
                  </button>
                ))}
              </div>
              <div className="min-h-[180px] flex-1 overflow-y-auto">
                {tab === 'assist' ? <InsightView assist={assist} /> : <TranscriptView segments={assist.segments} />}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function InsightView({ assist }: { assist: LiveAssistState }): JSX.Element {
  const [copied, copy] = useCopy()
  const i: Insight | null = assist.insight
  if (!i) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-10 text-center text-[12px] text-gray-500">
        {assist.insightLoading ? <Loader2 className="h-5 w-5 animate-spin text-[#3366FF]" /> : <Lightbulb className="h-5 w-5 text-gray-300" />}
        <p>{assist.insightLoading ? 'Reading the conversation…' : assist.insightError ?? 'Suggestions appear once the conversation gets going.'}</p>
      </div>
    )
  }
  const sentiment = SENTIMENT[i.sentiment] ?? SENTIMENT['neutral']!
  return (
    <div className="flex flex-col gap-3 p-3">
      {i.alerts.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {i.alerts.map((a, idx) => (
            <div key={idx} className={`flex items-start gap-2 rounded-[4px] border px-2.5 py-1.5 text-[12px] ${ALERT_CLS[a.type] ?? ALERT_CLS['info']}`}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span><b className="capitalize">{a.type.replace('_', ' ')}:</b> {a.message}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset ${sentiment.cls}`}>{sentiment.label}</span>
        {i.customerIntent && <span className="truncate text-[12px] text-gray-700"><span className="text-gray-400">Intent:</span> <b>{i.customerIntent}</b></span>}
      </div>
      {i.summary && <p className="text-[12.5px] leading-relaxed text-gray-700">{i.summary}</p>}
      {i.nextBestActions.length > 0 && (
        <section>
          <h4 className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">Next best actions</h4>
          <ol className="flex flex-col gap-1.5">
            {i.nextBestActions.map((a, idx) => (
              <li key={idx} className="flex gap-2 rounded-[4px] bg-[#F5F9FF] px-2.5 py-1.5 text-[12.5px] text-[#16273F]">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3366FF] text-[10px] font-bold text-white">{idx + 1}</span>{a}
              </li>
            ))}
          </ol>
        </section>
      )}
      {i.suggestedResponse && (
        <section className="rounded-[4px] border border-gray-200 px-2.5 py-2">
          <div className="mb-1 flex items-center justify-between">
            <h4 className="flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400"><MessageSquareQuote className="h-3 w-3" /> You could say</h4>
            <button type="button" onClick={() => copy('say', i.suggestedResponse)} className="text-gray-400 hover:text-gray-700" aria-label="Copy">
              {copied === 'say' ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <ClipboardCopy className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="text-[12.5px] italic text-gray-700">“{i.suggestedResponse}”</p>
        </section>
      )}
      {assist.config?.showKnowledge !== false && i.knowledge.length > 0 && (
        <section>
          <h4 className="mb-1.5 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400"><BookOpen className="h-3 w-3" /> Knowledge base</h4>
          <div className="flex flex-col gap-1.5">
            {i.knowledge.map((k) => (
              <a key={k.id} href="/agent/knowledge-base" target="_blank" rel="noreferrer" className="block rounded-[4px] border border-gray-100 px-2.5 py-1.5 hover:border-[#9EC5F4] hover:bg-[#F5F9FF]">
                <p className="truncate text-[12px] font-semibold text-[#16273F]">{k.title}</p>
                <p className="line-clamp-2 text-[11px] text-gray-500">{k.snippet}</p>
              </a>
            ))}
          </div>
        </section>
      )}
      <footer className="flex items-center justify-between border-t border-gray-100 pt-2 text-[10.5px] text-gray-400">
        <span className="flex items-center gap-1">
          {assist.insightLoading && <Loader2 className="h-3 w-3 animate-spin" />}
          {i.createdAt ? `Updated ${new Date(i.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : ''}
        </span>
        <span className="flex items-center gap-1">
          <button type="button" onClick={() => void assist.sendFeedback('up')} className={`rounded p-1 ${i.feedback === 'up' ? 'text-emerald-600' : 'hover:text-gray-600'}`} aria-label="Helpful"><ThumbsUp className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => void assist.sendFeedback('down')} className={`rounded p-1 ${i.feedback === 'down' ? 'text-red-600' : 'hover:text-gray-600'}`} aria-label="Not helpful"><ThumbsDown className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => void assist.requestInsight()} disabled={assist.insightLoading} className="rounded p-1 hover:text-gray-600 disabled:opacity-40" aria-label="Refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
        </span>
      </footer>
    </div>
  )
}

export function TranscriptView({ segments }: { segments: TranscriptSegment[] }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  useEffect(() => {
    const el = ref.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
  }, [segments])
  if (segments.length === 0) return <p className="px-6 py-10 text-center text-[12px] text-gray-400">Listening… the transcript appears here as people speak.</p>
  return (
    <div
      ref={ref}
      onScroll={(e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40 }}
      className="flex h-full max-h-[460px] flex-col gap-1.5 overflow-y-auto p-3"
    >
      {segments.map((s) => (
        <div key={s.key} className={`flex ${s.speaker === 'agent' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[85%] rounded-[8px] px-2.5 py-1.5 text-[12.5px] leading-snug ${s.speaker === 'agent' ? 'rounded-br-[2px] bg-[#3366FF] text-white' : 'rounded-bl-[2px] bg-gray-100 text-gray-800'} ${s.final ? '' : 'opacity-60'}`}>
            <span className={`mb-0.5 block text-[9.5px] font-bold uppercase tracking-wide ${s.speaker === 'agent' ? 'text-white/70' : 'text-gray-400'}`}>{s.speaker === 'agent' ? 'You' : 'Customer'}</span>
            {s.text}
          </div>
        </div>
      ))}
    </div>
  )
}

export function summaryText(s: CallSummary): string {
  return [
    `Summary: ${s.summary}`,
    s.reason && `Reason: ${s.reason}`,
    s.resolution && `Resolution: ${s.resolution}`,
    `Outcome: ${OUTCOME[s.outcome] ?? s.outcome}`,
    s.actionItems.length ? `Action items:\n${s.actionItems.map((a) => `- ${a}`).join('\n')}` : '',
    s.keyFacts.length ? `Key facts:\n${s.keyFacts.map((a) => `- ${a}`).join('\n')}` : '',
    s.followUp && `Follow-up: ${s.followUp}`,
  ].filter(Boolean).join('\n')
}

function SummaryView({ assist }: { assist: LiveAssistState }): JSX.Element {
  const [copied, copy] = useCopy()
  const s = assist.summary
  if (assist.summaryLoading && !s) {
    return <div className="flex flex-col items-center gap-2 px-6 py-10 text-center text-[12px] text-gray-500"><Loader2 className="h-5 w-5 animate-spin text-[#3366FF]" />Writing the call summary…</div>
  }
  if (!s) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-8 text-center text-[12px] text-gray-500">
        <p>{assist.summaryError ?? 'No summary yet.'}</p>
        {assist.segments.some((x) => x.final) && <button type="button" onClick={() => void assist.requestSummary()} className="rounded-[3px] border border-gray-200 px-3 py-1 text-[12px] font-semibold text-gray-700 hover:bg-gray-50">Try again</button>}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-3 overflow-y-auto p-3">
      <div className="flex items-center justify-between">
        <span className="rounded-full bg-[#EEF4FF] px-2 py-0.5 text-[10.5px] font-semibold text-[#3366FF]">{OUTCOME[s.outcome] ?? s.outcome}</span>
        <span className="flex gap-1">
          <button type="button" onClick={() => void assist.requestSummary()} disabled={assist.summaryLoading} className="rounded p-1 text-gray-400 hover:text-gray-700 disabled:opacity-40" title="Regenerate"><RefreshCw className={`h-3.5 w-3.5 ${assist.summaryLoading ? 'animate-spin' : ''}`} /></button>
          <button type="button" onClick={() => copy('sum', summaryText(s))} className="flex items-center gap-1 rounded-[3px] border border-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50">
            {copied === 'sum' ? <Check className="h-3 w-3 text-emerald-600" /> : <ClipboardCopy className="h-3 w-3" />} Copy
          </button>
        </span>
      </div>
      <p className="text-[12.5px] leading-relaxed text-gray-800">{s.summary}</p>
      <dl className="grid gap-2 text-[12px]">
        {s.reason && <div><dt className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">Reason</dt><dd className="text-gray-700">{s.reason}</dd></div>}
        {s.resolution && <div><dt className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">Resolution</dt><dd className="text-gray-700">{s.resolution}</dd></div>}
        {s.actionItems.length > 0 && (
          <div><dt className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">Action items</dt>
            <dd><ul className="list-disc pl-4 text-gray-700">{s.actionItems.map((a, i) => <li key={i}>{a}</li>)}</ul></dd></div>
        )}
        {s.keyFacts.length > 0 && (
          <div><dt className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">Key facts</dt>
            <dd className="flex flex-wrap gap-1">{s.keyFacts.map((a, i) => <span key={i} className="rounded-[3px] bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700">{a}</span>)}</dd></div>
        )}
        {s.followUp && <div><dt className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">Follow-up</dt><dd className="text-gray-700">{s.followUp}</dd></div>}
        {s.suggestedDisposition && <div><dt className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-400">Suggested disposition</dt><dd className="font-semibold text-gray-700">{s.suggestedDisposition}</dd></div>}
      </dl>
    </div>
  )
}
